import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from './Navbar';
import { AlertCircle, Home, Calendar, CheckSquare, FileSignature, ArrowRight, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import WelcomeDigestModal from './WelcomeDigestModal';
import InstallPrompt from './InstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [digestSending, setDigestSending] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchSummary();
    fetchPreferences();
  }, [navigate]);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/me/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrefs(response.data);
      if (response.data?.digest_welcome_shown === false) {
        setWelcomeOpen(true);
      }
    } catch (_) {
      // non-blocking — dashboard still renders without preferences
    }
  };

  const toggleDigest = async (next) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/users/me/preferences`,
        { digest_enabled: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrefs(response.data);
      toast.success(next ? 'Weekly digest enabled — first email lands Monday.' : 'Weekly digest disabled.');
    } catch (err) {
      toast.error('Failed to update preference');
    }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/digest/send-mine`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.sent) {
        toast.success(`Digest sent to ${response.data.to} (${response.data.items_count} items)`);
      } else if (response.data?.reason === 'no_items') {
        toast.info('Nothing to digest this week — no upcoming renewals or expiries.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setDigestSending(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="app-page">
      <Navbar />

      <main className="app-page-main dashboard-main">
        <InstallPrompt />
        {summary && (
          <div className="dashboard-summary-bar">
            <div className="summary-stats">
              <button
                type="button"
                className="summary-stat summary-stat-clickable"
                onClick={() => navigate('/properties')}
                data-testid="summary-properties-link"
              >
                <div className="summary-stat-value" data-testid="summary-properties">{summary.properties_count || 0}</div>
                <div className="summary-stat-label">Properties</div>
              </button>
              <button
                type="button"
                className="summary-stat summary-stat-clickable"
                onClick={() => navigate('/renewals')}
                data-testid="summary-renewals-link"
              >
                <div className="summary-stat-value" data-testid="summary-renewals">{summary.renewals_due_count || 0}</div>
                <div className="summary-stat-label">Renewals Due</div>
              </button>
              <button
                type="button"
                className="summary-stat summary-stat-clickable"
                onClick={() => navigate('/renewals?filter=urgent')}
                data-testid="summary-urgent-link"
              >
                <div className="summary-stat-value" data-testid="summary-urgent" style={{ color: 'var(--urgent)' }}>{summary.urgent_items_count || 0}</div>
                <div className="summary-stat-label">Urgent Items</div>
              </button>
            </div>
          </div>
        )}

        <div className="dashboard-cards">
          <div
            className="dashboard-card"
            onClick={() => navigate('/properties')}
            data-testid="card-properties"
          >
            <div className="card-icon">
              <Home size={40} />
            </div>
            <h2 className="card-title">Properties</h2>
            <p className="card-description">
              Manage your properties and tenant information
            </p>
          </div>

          <div
            className="dashboard-card"
            onClick={() => navigate('/renewals')}
            data-testid="card-renewals"
          >
            <div className="card-icon">
              <Calendar size={40} />
            </div>
            <h2 className="card-title">Renewals</h2>
            <p className="card-description">
              Track upcoming document renewals and expiry dates
            </p>
          </div>

          <div
            className="dashboard-card"
            onClick={() => navigate('/todo')}
            data-testid="card-todo"
          >
            <div className="card-icon">
              <CheckSquare size={40} />
            </div>
            <h2 className="card-title">To Do</h2>
            <p className="card-description">
              View and manage your tasks and reminders
            </p>
          </div>
        </div>

        {summary?.tenancies_ending && summary.tenancies_ending.length > 0 && (
          <div className="tenancies-ending-section" data-testid="tenancies-ending-panel">
            <div className="tenancies-ending-header">
              <FileSignature size={22} />
              <h2>Tenancies Ending Soon</h2>
              <span className="tenancies-ending-sub">{summary.tenancies_ending.length} within next 90 days</span>
            </div>
            <div className="tenancies-ending-items">
              {summary.tenancies_ending.map((item, idx) => {
                const days = item.days_remaining;
                const urgency = days < 0 ? 'urgency-urgent' : days <= 30 ? 'urgency-urgent' : days <= 60 ? 'urgency-warning' : 'urgency-normal';
                const chipLabel = days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`;
                return (
                  <div
                    key={idx}
                    className={`tenancies-ending-item ${urgency}`}
                    data-testid={`tenancy-ending-item-${idx}`}
                  >
                    <div
                      className="tenancies-ending-info"
                      onClick={() => navigate(`/property/${item.property_id}`, { state: { from: '/dashboard' } })}
                      data-testid={`tenancy-ending-open-${idx}`}
                    >
                      <div className="tenancies-ending-address">{item.property_address}</div>
                      <div className="tenancies-ending-meta">
                        {item.tenant_name && <span>{item.tenant_name}</span>}
                        {item.rent_amount != null && item.rent_amount > 0 && <span>£{item.rent_amount}/mo</span>}
                        <span>Ends {item.tenancy_end_date}</span>
                      </div>
                    </div>
                    <div className="tenancies-ending-actions">
                      <span className={`urgency-chip ${urgency}`}>{chipLabel}</span>
                      <button
                        type="button"
                        className="btn-email-renewal-cta"
                        onClick={() => navigate(`/property/${item.property_id}`, { state: { openEmailRenewal: true, from: '/dashboard' } })}
                        data-testid={`tenancy-ending-email-${idx}`}
                        title={item.tenant_email ? `Email ${item.tenant_email} about renewal` : 'Email tenant about renewal'}
                      >
                        <Mail size={14} />
                        Email Tenant
                      </button>
                      <button
                        type="button"
                        className="btn-new-tenancy-cta"
                        onClick={() => navigate(`/property/${item.property_id}`, { state: { openNewTenancy: true, from: '/dashboard' } })}
                        data-testid={`tenancy-ending-cta-${idx}`}
                        title="Start a new tenancy on this property"
                      >
                        <FileSignature size={14} />
                        New Tenancy
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {summary?.urgent_items && summary.urgent_items.length > 0 && (
          <div className="urgent-section" data-testid="urgent-preview">
            <div className="urgent-header">
              <AlertCircle size={24} />
              <h2>Urgent Documents Expiring Soon</h2>
            </div>
            <div className="urgent-items">
              {summary.urgent_items.map((item, index) => (
                <div
                  key={index}
                  className="urgent-item"
                  onClick={() => navigate(`/property/${item.property_id}`, { state: { from: '/dashboard' } })}
                  data-testid={`urgent-item-${index}`}
                >
                  <div className="urgent-item-info">
                    <div className="urgent-doc-type">{item.document_type.replace(/_/g, ' ')}</div>
                    <div className="urgent-address">{item.property_address}</div>
                  </div>
                  <div className="urgent-countdown">
                    <span className="days-number">{item.days_remaining}</span>
                    <span className="days-label">days left</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {prefs && (
          <div className="digest-controls" data-testid="digest-controls">
            <div className="digest-controls-left">
              <Mail size={18} />
              <div>
                <div className="digest-controls-title">Weekly email digest</div>
                <div className="digest-controls-sub">
                  {prefs.digest_enabled
                    ? 'On — sent every Monday at 08:00 (UK time).'
                    : 'Off — turn on to get a weekly summary every Monday.'}
                </div>
              </div>
            </div>
            <div className="digest-controls-right">
              <button
                type="button"
                className={`digest-toggle ${prefs.digest_enabled ? 'on' : 'off'}`}
                role="switch"
                aria-checked={prefs.digest_enabled}
                onClick={() => toggleDigest(!prefs.digest_enabled)}
                data-testid="digest-toggle"
              >
                <span className="digest-toggle-thumb" />
                <span className="sr-only">{prefs.digest_enabled ? 'Disable' : 'Enable'} weekly digest</span>
              </button>
              <button
                type="button"
                className="btn-send-digest-now"
                onClick={sendDigestNow}
                disabled={digestSending}
                data-testid="digest-send-now"
                title="Preview by sending this week's digest to your inbox now"
              >
                <Send size={14} />
                {digestSending ? 'Sending…' : 'Send my digest now'}
              </button>
            </div>
          </div>
        )}
      </main>

      <WelcomeDigestModal
        open={welcomeOpen}
        onResolved={(enabled) => {
          setWelcomeOpen(false);
          setPrefs((p) => ({ ...(p || {}), digest_enabled: enabled, digest_welcome_shown: true }));
        }}
      />
    </div>
  );
}
